import { useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Label,
  CartesianGrid,
  CartesianAxis,
  ResponsiveContainer,
  BarChart,
  Bar,
  Text,
  Cell,
} from "recharts";
import { useAppContext } from "../../context/AppContext";

export function FrequencyGraph({ data, xAxisKey, yAxisKey }) {
  function CustomTooltip({ payload, label, active }) {
    if (active && payload) {
      return (
        <div
          className="custom-tooltip p-2 rounded-lg outline-none"
          style={{
            background:
              "linear-gradient(178.95deg, #3E0CA7 5.08%, #0B28AC 100%)",
          }}
        >
          <p className="text-white  font-bold">Frequency: {payload[0].value}</p>
          <p className="text-white font-bold">{payload[0].payload[xAxisKey]}</p>
        </div>
      );
    }

    return "";
  }

  function formatDate(value) {
    const date = new Date(value);
    return (
      date.toDateString().split(" ")[2] +
      " " +
      date.toDateString().split(" ")[1]
    );
  }

  function CustomXAxisTick({
    x,
    y,
    payload,
    width = 10,
    maxChars = 10,
    lineHeight = 14,
    fontSize = 14,
    fill = "#FFF",
  }) {
    if (!payload.value) return <p></p>;
    const rx = new RegExp(`.{1,${maxChars}}`, "g");
    const chunks = formatDate(payload.value)
      .replace(/-/g, " ")
      .split(" ")
      .map((s) => s.match(rx))
      .flat();
    const tspans = chunks.map((s, i) => (
      <tspan x={0} y={lineHeight} dy={i * lineHeight}>
        {s}
      </tspan>
    ));
    return (
      <g transform={`translate(${x},${y})`}>
        <text
          width={width}
          height="auto"
          textAnchor="middle"
          fontSize={fontSize}
          fill={fill}
          fontWeight={700}
        >
          {tspans}
        </text>
      </g>
    );
  }

  const appState = useAppContext();

  return (
    <ResponsiveContainer width={"100%"} height={350}>
      <LineChart
        data={data}
        margin={{ top: 0, left: 10, right: 20, bottom: 30 }}
      >
        <Line
          type="monotone"
          dataKey={yAxisKey}
          strokeLinecap="round"
          strokeWidth={3}
          dot={false}
          stroke="#34E130"
        />
        <XAxis
          stroke={appState.state.darkMode ? "#fff" : "#232733"}
          dataKey={xAxisKey}
          axisLine={false}
          tick={<CustomXAxisTick />}
          tickFormatter={formatDate}
        >
          <Label
            value="Dates"
            offset={-30}
            position="insideBottom"
            fill={appState.state.darkMode ? "#fff" : "#232733"}
            fontWeight={700}
            textAnchor="middle"
          />
        </XAxis>
        <YAxis
          stroke={appState.state.darkMode ? "#fff" : "#232733"}
          axisLine={false}
          allowDecimals={false}
        >
          <Label
            value="Frequency (Number of Exercises)"
            offset={10}
            angle={-90}
            fill={appState.state.darkMode ? "#fff" : "#232733"}
            fontWeight={700}
            position="insideLeft"
            style={{ textAnchor: "middle" }}
          />
        </YAxis>
        <CartesianAxis
          axisLine={false}
          fill={appState.state.darkMode ? "#fff" : "#232733"}
        />
        <CartesianGrid
          vertical={false}
          strokeDasharray="2 2"
          stroke={appState.state.darkMode ? "#fff" : "#232733"}
        />
        <Tooltip content={<CustomTooltip />} />
      </LineChart>
    </ResponsiveContainer>
  );
}

export function AccuracyGraph({ data, xAxisKey, yAxisKey }) {
  function CustomXAxisTick({
    x,
    y,
    payload,
    width = 10,
    maxChars = 10,
    lineHeight = 14,
    fontSize = 14,
    fill = "#FFF",
  }) {
    if (!payload.value) return <p></p>;
    const rx = new RegExp(`.{1,${maxChars}}`, "g");
    const chunks = payload.value
      .replace(/-/g, " ")
      .split(" ")
      .map((s) => s.match(rx))
      .flat();
    const tspans = chunks.map((s, i) => (
      <tspan x={0} y={lineHeight} dy={i * lineHeight}>
        {s}
      </tspan>
    ));
    return (
      <g transform={`translate(${x},${y})`}>
        <text
          width={width}
          height="auto"
          textAnchor="middle"
          fontSize={fontSize}
          fill={fill}
          fontWeight={700}
        >
          {tspans}
        </text>
      </g>
    );
  }
  function CustomTooltip({ payload, label, active }) {
    if (active && payload) {
      return (
        <div
          className="custom-tooltip p-2 rounded-lg"
          style={{
            background:
              "linear-gradient(178.95deg, #A17DEF 2.75%, #1A17B4 77.71%)",
          }}
        >
          <p className="text-white font-bold">{payload[0].payload[xAxisKey]}</p>
        </div>
      );
    }

    return "";
  }

  const appState = useAppContext();

  return (
    <ResponsiveContainer width={"100%"} height={350}>
      <BarChart
        data={data}
        margin={{ top: 0, left: 10, right: 20, bottom: 30 }}
      >
        <defs>
          <linearGradient
            id="colorUv"
            x1="0"
            y1="0"
            x2="0"
            y2="100%"
            spreadMethod="reflect"
          >
            <stop offset="0" stopColor="#A17DEF" />
            <stop offset="1" stopColor="#1A17B4" />
          </linearGradient>
        </defs>
        <Bar
          type="monotone"
          dataKey={yAxisKey}
          fill="url(#colorUv)"
          radius={4}
          maxBarSize={30}
        />
        <XAxis
          stroke={appState.state.darkMode ? "#fff" : "#232733"}
          dataKey={xAxisKey}
          tick={<CustomXAxisTick />}
          interval={0}
          axisLine={false}
          fontSize={12}
          accentHeight={20}
          height={40}
        >
          <Label
            value="Exercises"
            position="bottom"
            fill={appState.state.darkMode ? "#fff" : "#232733"}
            fontSize={14}
            fontWeight={700}
            textAnchor="middle"
          />
        </XAxis>
        <YAxis
          stroke={appState.state.darkMode ? "#fff" : "#232733"}
          axisLine={false}
          // strokeWidth={3}
          allowDecimals={false}
          domain={[0, 100]}
          type="number"
          alignmentBaseline="auto"
        >
          <Label
            value="Accuracy (Percentage)"
            offset={10}
            angle={-90}
            fill={appState.state.darkMode ? "#fff" : "#232733"}
            fontWeight={700}
            position="insideLeft"
            style={{ textAnchor: "middle" }}
          />
        </YAxis>
        <CartesianGrid vertical={false} horizontal={false} />
        <Tooltip content={<CustomTooltip />} cursor={false} />
      </BarChart>
    </ResponsiveContainer>
  );
}

export function PerformanceGraph({ data, xAxisKey, yAxisKey }) {
  const appState = useAppContext();
  return (
    <ResponsiveContainer width={"100%"} height={350}>
      <BarChart
        data={data}
        margin={{ top: 0, left: -10, right: 20, bottom: 25 }}
        layout="vertical"
      >
        <defs>
          <linearGradient
            id="colorAC"
            y1="188"
            x1="0.330078"
            y2="188"
            x2="182.33"
            gradientUnits="userSpaceOnUse"
          >
            <stop stop-color="#1A59DB" stop-opacity="0.718854" />
            <stop offset="1" stop-color="#06E5E5" />
          </linearGradient>
        </defs>
        <Bar
          type="monotone"
          dataKey={xAxisKey}
          fill="url(#colorAC)"
          radius={4}
          maxBarSize={20}
        />
        <XAxis
          stroke={appState.state.darkMode ? "#fff" : "#232733"}
          type="number"
          fill={appState.state.darkMode ? "#fff" : "#232733"}
          domain={[0, 100]}
          fontSize={14}
          tickFormatter={(item) => item + " %"}
          axisLine={false}
        >
          <Label
            value="Top Performance"
            offset={-20}
            position="insideBottom"
            fill={appState.state.darkMode ? "#fff" : "#232733"}
            fontSize={14}
            fontWeight={700}
            textAnchor="middle"
          />
        </XAxis>
        <YAxis
          stroke={appState.state.darkMode ? "#fff" : "#232733"}
          type="category"
          dataKey={yAxisKey}
          fontSize={12}
          fontWeight={700}
          tickFormatter={(item) => (item ? item.toUpperCase() : "")}
          fill={appState.state.darkMode ? "#fff" : "#232733"}
          width={150}
          axisLine={false}
        />
        <CartesianGrid
          horizontal={false}
          strokeDasharray="2 8"
          stroke="#FFFFFF66"
        />
      </BarChart>
    </ResponsiveContainer>
  );
}

export function LeaderboardGraph({ data, xAxisKey, yAxisKey }) {
  return (
    <ResponsiveContainer width={"100%"} height={350}>
      <BarChart
        data={data}
        margin={{ top: 0, left: -10, right: 20, bottom: 25 }}
        layout="vertical"
      >
        <defs>
          <linearGradient
            id="colorAC"
            y1="188"
            x1="0.330078"
            y2="188"
            x2="182.33"
            gradientUnits="userSpaceOnUse"
          >
            <stop stop-color="#1A59DB" stop-opacity="0.718854" />
            <stop offset="1" stop-color="#06E5E5" />
          </linearGradient>
        </defs>
        <Bar
          type="monotone"
          dataKey={xAxisKey}
          fill="url(#colorAC)"
          radius={4}
          maxBarSize={20}
        />
        <XAxis
          stroke="#FFF"
          type="number"
          fill="#FFF"
          fontSize={14}
          axisLine={false}
        >
          <Label
            value="Coins"
            offset={-25}
            position="insideBottom"
            fill="#FFF"
            fontSize={14}
            fontWeight={700}
            textAnchor="middle"
          />
        </XAxis>
        <YAxis
          stroke="#FFF"
          type="category"
          dataKey={yAxisKey}
          fontSize={14}
          fontWeight={700}
          fill="#FFF"
          width={150}
          axisLine={false}
        />
        <CartesianGrid
          horizontal={false}
          strokeDasharray="2 8"
          stroke="#FFFFFF66"
        />
      </BarChart>
    </ResponsiveContainer>
  );
}
